import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGem, faChevronLeft, faChevronRight, faTimes, faFilm } from '@/icons';
import { useTheme } from '../contexts/ThemeContext';
import { useFeatureFlagContext } from '../contexts/FeatureFlagContext';
import { enabledProFeatures } from '../constants/proFeatureCatalog';

interface ProFeatureTourModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpgradeClick?: () => void;
}

export const ProFeatureTourModal: React.FC<ProFeatureTourModalProps> = ({ isOpen, onClose, onUpgradeClick }) => {
    const theme = useTheme();
    const { flags } = useFeatureFlagContext();
    const [currentIndex, setCurrentIndex] = useState(0);

    if (!isOpen) return null;

    const visibleFeatures = enabledProFeatures(flags);

    const safeIndex = Math.min(currentIndex, Math.max(0, visibleFeatures.length - 1));
    const current = visibleFeatures[safeIndex];
    const total = visibleFeatures.length;

    if (!current) return null;

    const goNext = () => setCurrentIndex(i => (i + 1) % total);
    const goPrev = () => setCurrentIndex(i => (i - 1 + total) % total);


    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 2000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: theme.spacing.lg,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div style={{
                backgroundColor: theme.colors.surface,
                borderRadius: theme.borderRadius.lg,
                boxShadow: theme.shadows.lg,
                width: '860px',
                maxWidth: '100%',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                    backgroundColor: theme.colors.primary,
                    flexShrink: 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                        <FontAwesomeIcon icon={faGem} style={{ color: '#c8a8f0', fontSize: theme.typography.fontSize.lg }} />
                        <span style={{
                            color: theme.colors.text.inverse,
                            fontWeight: theme.typography.fontWeight.bold,
                            fontSize: theme.typography.fontSize.lg,
                            letterSpacing: '0.5px',
                        }}>
                            Pro Features
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: theme.colors.text.inverse,
                            padding: theme.spacing.xs,
                            opacity: 0.8,
                            fontSize: theme.typography.fontSize.md,
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Body */}
                <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
                    {/* Sidebar */}
                    <div style={{
                        width: '220px',
                        flexShrink: 0,
                        borderRight: `1px solid ${theme.colors.border.default}`,
                        overflowY: 'auto',
                        backgroundColor: theme.colors.background,
                    }}>
                        {visibleFeatures.map((feature, index) => (
                            <button
                                key={feature.key}
                                onClick={() => setCurrentIndex(index)}
                                style={{
                                    width: '100%',
                                    textAlign: 'left',
                                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                    border: 'none',
                                    borderRadius: 0,
                                    backgroundColor: index === safeIndex
                                        ? theme.colors.primary
                                        : 'transparent',
                                    cursor: 'pointer',
                                    fontSize: theme.typography.fontSize.sm,
                                    fontWeight: index === safeIndex
                                        ? theme.typography.fontWeight.semibold
                                        : theme.typography.fontWeight.normal,
                                    color: index === safeIndex
                                        ? theme.colors.text.inverse
                                        : theme.colors.text.secondary,
                                    transition: `all ${theme.transitions.fast}`,
                                    lineHeight: theme.typography.lineHeight.normal,
                                }}
                            >
                                {feature.label}
                            </button>
                        ))}
                    </div>

                    {/* Main content */}
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        padding: theme.spacing.lg,
                        gap: theme.spacing.md,
                    }}>
                        {/* Feature title */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.sm,
                        }}>
                            <FontAwesomeIcon
                                icon={faGem}
                                style={{ color: theme.colors.primary, fontSize: theme.typography.fontSize.sm }}
                            />
                            <span style={{
                                fontSize: theme.typography.fontSize.xl,
                                fontWeight: theme.typography.fontWeight.bold,
                                color: theme.colors.text.primary,
                            }}>
                                {current.label}
                            </span>
                        </div>

                        {/* Gif area */}
                        <div style={{
                            flex: 1,
                            minHeight: '240px',
                            backgroundColor: theme.colors.background,
                            borderRadius: theme.borderRadius.md,
                            border: `2px dashed ${theme.colors.border.default}`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: theme.spacing.sm,
                            overflow: 'hidden',
                        }}>
                            {current.videoPath ? (
                                <video
                                    key={current.videoPath}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: theme.borderRadius.md }}
                                >
                                    <source src={current.videoPath} type="video/mp4" />
                                </video>
                            ) : (
                                <>
                                    <FontAwesomeIcon
                                        icon={faFilm}
                                        style={{ fontSize: '48px', color: theme.colors.border.default }}
                                    />
                                    <span style={{
                                        fontSize: theme.typography.fontSize.sm,
                                        color: theme.colors.text.muted,
                                    }}>
                                        Demo coming soon
                                    </span>
                                </>
                            )}
                        </div>

                        {/* Description */}
                        <p style={{
                            margin: 0,
                            fontSize: theme.typography.fontSize.md,
                            color: theme.colors.text.secondary,
                            lineHeight: theme.typography.lineHeight.relaxed,
                        }}>
                            {current.description}
                        </p>

                        {/* Navigation */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            paddingTop: theme.spacing.sm,
                            borderTop: `1px solid ${theme.colors.border.default}`,
                            flexShrink: 0,
                        }}>
                            <button
                                onClick={goPrev}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing.sm,
                                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                    backgroundColor: theme.colors.background,
                                    border: `1px solid ${theme.colors.border.default}`,
                                    borderRadius: theme.borderRadius.md,
                                    cursor: 'pointer',
                                    fontSize: theme.typography.fontSize.sm,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    color: theme.colors.text.primary,
                                }}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                                Back
                            </button>

                            <span style={{
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.colors.text.muted,
                            }}>
                                {currentIndex + 1} / {total}
                            </span>

                            <button
                                onClick={goNext}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing.sm,
                                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                    backgroundColor: theme.colors.primary,
                                    border: 'none',
                                    borderRadius: theme.borderRadius.md,
                                    cursor: 'pointer',
                                    fontSize: theme.typography.fontSize.sm,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    color: theme.colors.text.inverse,
                                }}
                            >
                                Next
                                <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Upgrade CTA */}
                {onUpgradeClick && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                        borderTop: `1px solid ${theme.colors.border.default}`,
                        flexShrink: 0,
                    }}>
                        <button
                            onClick={() => { onClose(); onUpgradeClick(); }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing.sm,
                                padding: `${theme.spacing.sm} ${theme.spacing.xl}`,
                                backgroundColor: theme.colors.primary,
                                border: 'none',
                                borderRadius: theme.borderRadius.md,
                                cursor: 'pointer',
                                fontSize: theme.typography.fontSize.md,
                                fontWeight: theme.typography.fontWeight.bold,
                                color: theme.colors.text.inverse,
                            }}
                        >
                            <FontAwesomeIcon icon={faGem} />
                            Upgrade to Pro
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
